import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  Select,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import { MdDelete, MdEdit, MdVisibility } from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Pagination from 'components/pagination';
import { useNavigate } from 'react-router-dom';
import CategoryModal from './components/CategoryModal';
import DeleteModal from 'components/deleteModal/DeleteModal';
import BackButton from 'components/BackButton';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';
import Loader from 'loaders/Loader';
import ErrorMessage from 'components/error-message';
import { getUserData } from 'utils';

// Column Helper
const columnHelper = createColumnHelper();

// TeamTable Component
export default function ManageCategories() {
  const userData = getUserData();
  const clinic_id = userData?.clinic_id ?? null;

  const [sorting, setSorting] = React.useState([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [selectedCategory, setSelectedCategory] = React.useState('');

  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [itemToDelete, setItemToDelete] = React.useState('');
  const [deletePayload, setDeletePayload] = React.useState({});

  const openDeleteModalForItem = (item) => {
    setItemToDelete(item);
    setOpenDeleteModal(true);
  };

  const handleDeleteCategory = async () => {
    try {
      const response = await createAPIEndPoint('service_categories/').delete(
        itemToDelete.id,
      );
      toast.success('Category deleted successfully!');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error deleting category');
    }
  };

  const [data, setData] = React.useState([]);
  const [filteredData, setFilteredData] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  // Fetch categories and set both data and filteredData
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await createAPIEndPoint(
        `service_categories/get_all/${clinic_id}`,
      ).fetchAll();
      const categories = response.data.service_categories || [];
      setData(categories);
      setFilteredData(categories);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(
        err?.response?.data?.error || 'Error fetching service categories',
      );
    }
  };

  React.useEffect(() => {
    fetchCategories();
  }, []);

  // Columns definition
  const columns = [
    columnHelper.accessor((row, index) => index + 1, {
      id: '#',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          #
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('category', {
      id: 'category',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Category
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('action', {
      id: 'action',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          ACTION
        </Text>
      ),
      cell: (info) => {
        const handleEditClick = (e) => {
          onOpen();
          setSelectedCategory(info.row.original);
          setType('edit');
        };

        const handleDeleteClick = () => {
          openDeleteModalForItem(info.row.original);
        };
        return (
          <Box display="flex">
            <IconButton
              icon={<MdEdit style={{ marginLeft: '-1px' }} />}
              aria-label="Edit Details"
              colorScheme="brand"
              size="sm"
              mr="6px"
              onClick={handleEditClick}
            />
            <IconButton
              icon={<MdDelete style={{ marginLeft: '-1px' }} />}
              aria-label="View Details"
              colorScheme="red"
              size="sm"
              onClick={handleDeleteClick}
            />
          </Box>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  // Handle search logic
  React.useEffect(() => {
    if (!searchTerm) {
      setFilteredData(data); // Reset to original data when search is cleared
    } else {
      const filtered = data.filter((item) =>
        item.category?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredData(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, data]);

  const itemsPerPageOptions = [10, 20, 30, 50];
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const [type, setType] = React.useState('add');
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      {loading && <Loader />}
      <Box pt={{ base: '80px', md: '80px', xl: '80px' }}>
        <BackButton />
        <Card flexDirection="column" w="100%" px="0px">
          <Flex
            px={{ base: '16px', md: '25px' }}
            mb="8px"
            justify="space-between"
            align="center"
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 4, md: 0 }}
          >
            <Text
              color={textColor}
              fontSize="24px"
              fontWeight="700"
              lineHeight="100%"
              mb={{ base: '8px', md: '0px' }}
            >
              Manage Categories
            </Text>

            <Box
              w={{ base: '100%', md: 'auto' }}
              display="flex"
              justifyContent={{ base: 'center', md: 'flex-end' }}
              ml="auto"
              mr={{ md: 5 }}
            >
              <SearchBar
                width="100%"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />{' '}
            </Box>

            <Box w={{ base: '100%', md: 'auto' }}>
              <Button
                w={{ base: '100%', md: 'fit-content' }}
                mr={{ base: '0', md: '8px' }}
                minW="140px"
                variant="brand"
                fontWeight="500"
                onClick={() => {
                  onOpen();
                  setType('add');
                }}
              >
                Add Category
              </Button>
            </Box>
          </Flex>

          <Flex
            px={{ base: '16px', md: '25px' }}
            mb="8px"
            justify="space-between"
            align="center"
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 4, md: 0 }}
          >
            <Box w={{ base: '100%', md: 'auto' }}>
              <Select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                width={{ base: '100%', md: 'fit-content' }}
                borderRadius="16px"
              >
                {itemsPerPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option} per page
                  </option>
                ))}
              </Select>
            </Box>

            <Box
              w={{ base: '100%', md: 'auto' }}
              display="flex"
              justifyContent={{ base: 'center', md: 'flex-end' }}
            >
              <Pagination
                totalPages={totalPages}
                initialPage={currentPage}
                onPageChange={handlePageChange}
              />{' '}
            </Box>
          </Flex>

          <Box overflowX="auto">
            <Table variant="striped" color="gray.500" mb="24px" mt="12px">
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        pe="10px"
                        borderColor={borderColor}
                        cursor="pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Flex justifyContent="space-between" align="center">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {paginatedData.map((row, index) => (
                  <Tr key={index}>
                    {table
                      .getRowModel()
                      .rows.find((r) => r.original === row) // Ensure correct row mapping
                      ?.getVisibleCells()
                      ?.map((cell) => (
                        <Td
                          key={cell.id}
                          fontSize="14px"
                          borderColor="transparent"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Td>
                      ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          {!loading &&
            filteredData.length === 0 &&
            (searchTerm.length > 0 ? (
              <ErrorMessage source="results" />
            ) : (
              <ErrorMessage source="categories" />
            ))}
        </Card>
        <CategoryModal
          isOpen={isOpen}
          onClose={onClose}
          type={type}
          initialData={selectedCategory}
          fetchAgain={fetchCategories}
        />

        <DeleteModal
          isOpen={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          selectedItem={itemToDelete.category}
          onDelete={handleDeleteCategory}
        />
      </Box>
    </>
  );
}
